import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { LeagueEvent, Player, Team } from '../../../sdk/model';
import { TableRow, Typography, Box, Card, CardContent, Chip, useMediaQuery } from '@mui/material';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import { styled, useTheme } from '@mui/material/styles';
import DraftQuickDisplay from './DraftQuickDisplay';
import AuctionQuickDisplay from './AuctionQuickDisplay';
import TradeAtAGlance from '../trade/TradeAtAGlance';
import WaiverFaabEventQuickView from './WaiverFaabEventQuickView';
import PlayerCardLink from '../../cards/playerCardLink';
import ContractTagDisplay from './ContractTagDisplay';
import QuickSignDisplay from './QuickSignDisplay';
import IrTransactionDisplay from './IrTransactionDisplay';
import MidSeasonExtensionDisplay from './MidSeasonExtensionDisplay';
import PracticeSquadActivationDisplay from './PracticeSquadActivationDisplay';
import TeamDisplay from '../team/TeamDisplay';
import { toSalary } from '../../util/SalaryDisplay';
import { leagueEventApi } from '../../../adapters/APIExporter';
import ReactionsModule from '../../util/ReactionsModule';

interface leagueEventDisplayProps {
  event: LeagueEvent,
  playerMap: Map<string | undefined, Player | undefined>,
  teamMap: Map<string | undefined, Team>
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: theme.palette.common.black,
      color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
    },
  }));
  
const EVENT_TYPE_LINK_CREATOR_MAP = new Map();
EVENT_TYPE_LINK_CREATOR_MAP.set('draft', event => '/league/' + event.leagueId + '/draftHistory?seasonYear=' + event.seasonYear)
EVENT_TYPE_LINK_CREATOR_MAP.set('auction', event => '/league/' + event.leagueId + '/auctionHistory?seasonYear=' + event.seasonYear)
EVENT_TYPE_LINK_CREATOR_MAP.set('trade', event => '/league/' + event.leagueId + '/tradeHistory/' + event.id)
EVENT_TYPE_LINK_CREATOR_MAP.set('waiver_faab_event', event => '/league/' + event.leagueId + '/playerAcquisitionEvent/' + event.id)
EVENT_TYPE_LINK_CREATOR_MAP.set('player_quick_sign_event', event => '/league/' + event.leagueId + '/playerAcquisitionEvent/' + event.id)


function getEventTypeDisplay(type) {
  if (type === 'waiver_faab_event') {
    return "Waiver/FAAB Event"
  }
  if (type === 'player_drop_event') {
    return "Player Dropped"
  }
  if (type === 'ir_transaction') {
    return "IR Transaction"
  } if (type === 'mid_season_extension_event') {
    return "Mid-Season Extension"
  } if (type === 'practice_squad_activation_event') {
    return "Practice Squad Activation"
  }
  return type.split("_").map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
}

function LeagueEventDisplay(props: leagueEventDisplayProps) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const eventTypeDisplayMap = new Map();
  eventTypeDisplayMap.set('draft', event => <DraftQuickDisplay draft={event} />)
  eventTypeDisplayMap.set('auction', event => <AuctionQuickDisplay auction={event} />)
  eventTypeDisplayMap.set('trade', event => <TradeAtAGlance leagueId={event.leagueId} tradeStatus={event.status} teams={event.tradeProposal.teams} picks={event.tradeProposal.picks} players={event.tradeProposal.players} playerMap={props.playerMap} teamMap={props.teamMap} pickMap={undefined} />)
  eventTypeDisplayMap.set('waiver_faab_event', event => <WaiverFaabEventQuickView event={event} playerMap={props.playerMap} teamMap={props.teamMap} />)
  eventTypeDisplayMap.set('player_drop_event', event => {
    const player = props.playerMap.get(event.playerId)!;
    return <Typography>
      <PlayerCardLink player={player} leagueId={event.leagueId} /> was dropped by <TeamDisplay leagueId={props.event.leagueId} team={props.teamMap.get(event.teamId)!} emphasized={false} teamId={event.teamId} />
    </Typography>
  })
  eventTypeDisplayMap.set('contract_import', event => 
    <Typography>{event.numberOfImportedContracts} contracts imported</Typography>
  )
  eventTypeDisplayMap.set('contract_tag', event => <ContractTagDisplay contractTag={event} playerMap={props.playerMap} team={props.teamMap.get(event.teamId)!} leagueId={event.leagueId} />)
  eventTypeDisplayMap.set('player_quick_sign_event', event => <QuickSignDisplay event={event} playerMap={props.playerMap} teamMap={props.teamMap} />)
  eventTypeDisplayMap.set('ir_transaction', event => <IrTransactionDisplay event={event} playerMap={props.playerMap} teamMap={props.teamMap} />)
  eventTypeDisplayMap.set('mid_season_extension_event', event => <MidSeasonExtensionDisplay leagueId={event.leagueId} teamId={event.teamId} details={event.details} playerMap={props.playerMap} teamMap={props.teamMap} />)
  eventTypeDisplayMap.set('practice_squad_activation_event', event => <PracticeSquadActivationDisplay leagueId={event.leagueId} teamId={event.teamId} playerMap={props.playerMap} teamMap={props.teamMap} playerId={event.playerId} amount={event.amount} />)
  eventTypeDisplayMap.set('delete_team', event => <Typography><TeamDisplay leagueId={props.event.leagueId} team={props.teamMap.get(event.teamId)!} emphasized={false} teamId={event.teamId} /> was removed from the league</Typography>)
  eventTypeDisplayMap.set('commissioner_add_player_event', event => {
      const player = props.playerMap.get(event.playerId)!;
      return <Typography>
        <PlayerCardLink player={player} leagueId={event.leagueId} /> was add to <TeamDisplay leagueId={props.event.leagueId} team={props.teamMap.get(event.teamId)!} emphasized={false} teamId={event.teamId} /> for <Chip label={toSalary(event.amount) + '/' + event.years + "Y"} color="primary" size="small" /> by the Commissioner
      </Typography>
    })
  eventTypeDisplayMap.set('contract_edit_event', event => {
      const player = props.playerMap.get(event.playerId)!;
      return <Typography>
        Contract for <PlayerCardLink player={player} leagueId={event.leagueId} /> on <TeamDisplay leagueId={props.event.leagueId} team={props.teamMap.get(event.teamId)!} emphasized={false} teamId={event.teamId} /> was modified to <Chip label={toSalary(event.amount) + '/' + event.years + "Y"} color="primary" size="small" /> by the Commissioner
      </Typography>
    })
  
  const eventDateTime = new Date(props.event.eventDateTime);
  const linkCreator = EVENT_TYPE_LINK_CREATOR_MAP.get(props.event.type);
  const link = linkCreator ? linkCreator(props.event) : null;
  
  // Mobile layout - Card based for better mobile UX
  if (isMobile) {
    return (
      <Card 
        sx={{ 
          mb: 2, 
          mx: 1,
          borderLeft: 4,
          borderLeftColor: theme.palette.primary.main,
        }}
        elevation={2}
      >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              {eventDateTime.toLocaleString()}
            </Typography>
            <Chip 
              label={getEventTypeDisplay(props.event.type)}
              size="small"
              color="primary"
              variant="outlined"
              onClick={link ? () => navigate(link) : undefined}
              sx={{ 
                cursor: link ? 'pointer' : 'default',
                fontSize: '0.7rem',
                height: 24
              }}
            />
          </Box>
          <Box sx={{ mt: 1 }}>
            {eventTypeDisplayMap.get(props.event.type)(props.event)}
            <ReactionsModule reactions={props.event.reactions!} 
                addReaction={emoji => leagueEventApi.addEventReaction(props.event.id, emoji)}
                removeReaction={emoji => leagueEventApi.removeEventReaction(props.event.id, emoji)} />
          </Box>
        </CardContent>
      </Card>
    );
  }
  
  // Desktop layout - Enhanced table row
  const getEventTypeColor = () => {
    switch(props.event.type) {
      case 'trade': return 'primary';
      case 'waiver_faab_event': return 'warning';
      case 'draft': return 'success';
      case 'auction': return 'info';
      case 'contract_tag': return 'secondary';
      case 'player_drop_event': return 'error';
      default: return 'default';
    }
  };

  return <TableRow sx={{ 
    '&:hover': { 
      backgroundColor: theme.palette.action.hover,
      '& .MuiTableCell-root': {
        borderBottom: `2px solid ${theme.palette.primary.main}20`
      }
    },
    borderLeft: 4,
    borderLeftColor: theme.palette[getEventTypeColor()]?.main || theme.palette.grey[300],
    borderLeftStyle: 'solid'
  }}>
    <StyledTableCell sx={{ minWidth: '140px' }}>
      <Typography variant="caption" color="text.secondary">
        {eventDateTime.toLocaleString()}
      </Typography>
    </StyledTableCell>
    <StyledTableCell sx={{ minWidth: '160px' }}>
      {link ? (
        <Chip
          label={getEventTypeDisplay(props.event.type)}
          color={getEventTypeColor()}
          size="small"
          onClick={() => navigate(link)}
          sx={{ cursor: 'pointer', '&:hover': { transform: 'scale(1.05)' } }}
        />
      ) : (
        <Chip
          label={getEventTypeDisplay(props.event.type)}
          color={getEventTypeColor()}
          size="small"
          variant="outlined"
        />
      )}
    </StyledTableCell>
    <StyledTableCell sx={{ paddingY: 1.5 }}>
      <Box sx={{ py: 0.5 }}>
        {eventTypeDisplayMap.get(props.event.type)(props.event)}
        <ReactionsModule reactions={props.event.reactions!} 
            addReaction={emoji => leagueEventApi.addEventReaction(props.event.id, emoji)}
            removeReaction={emoji => leagueEventApi.removeEventReaction(props.event.id, emoji)} />
      </Box>
    </StyledTableCell>
  </TableRow>;
}

export default LeagueEventDisplay;